import {
    CheckCircleOutline,
    DeleteOutline,
    DoneAll,
    NotificationsNone,
    OpenInNew,
} from '@mui/icons-material';
import {
    Alert,
    Badge,
    Box,
    Button,
    CircularProgress,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Popover,
    Snackbar,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import {
    isAppInboxLiveNotification,
    LIVE_INBOX_KINDS,
    SUBSCRIPTION_PAGE_PATH,
    subscribeLiveNotifications,
} from '../helpers/liveNotifications';

const POLL_MS = 60000;
const TOAST_BODY_MAX_CHARS = 100;

function formatWhen(value) {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function resolveAppLink(link) {
    const raw = String(link || '').trim();
    if (!raw) {
        return null;
    }
    if (/^https?:\/\//i.test(raw)) {
        return { type: 'external', href: raw };
    }
    if (raw.startsWith('/admin')) {
        return { type: 'internal', href: raw };
    }
    if (raw.startsWith('/')) {
        return { type: 'internal', href: `/admin${raw}` };
    }
    return { type: 'internal', href: `/admin/${raw}` };
}

function truncateToastBody(text) {
    const body = String(text || '').replace(/\s+/g, ' ').trim();
    if (!body) {
        return '';
    }
    if (body.length <= TOAST_BODY_MAX_CHARS) {
        return body;
    }
    return `${body.slice(0, TOAST_BODY_MAX_CHARS).trimEnd()}…`;
}

function buildToastBody(detail) {
    const baseBody = String(detail?.body || '').replace(/\s+/g, ' ').trim();
    if (
        detail?.inboxKind === LIVE_INBOX_KINDS.SUBSCRIPTION_CHARGE_READY
        && window.location.pathname === SUBSCRIPTION_PAGE_PATH
    ) {
        return truncateToastBody(`${baseBody} Recarregue a página para visualizar o boleto e o QR Code PIX.`);
    }
    return truncateToastBody(baseBody);
}

const NotificationInbox = () => {
    const navigate = useNavigate();
    const openRef = useRef(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const [items, setItems] = useState([]);
    const [loadingList, setLoadingList] = useState(false);
    const [busyId, setBusyId] = useState(null);
    const [toast, setToast] = useState(null);
    const open = Boolean(anchorEl);
    openRef.current = open;

    const refreshUnread = useCallback(async () => {
        try {
            const response = await api.get('/notifications/unread-count');
            const count = response?.data?.data?.unread_count ?? 0;
            setUnreadCount(Number(count) || 0);
        } catch {
            // silent — badge is best-effort
        }
    }, []);

    const loadList = useCallback(async () => {
        setLoadingList(true);
        try {
            const response = await api.get('/notifications', { params: { page: 1, limit: 30 } });
            const list = response?.data?.data?.notifications ?? [];
            setItems(Array.isArray(list) ? list : []);
        } catch {
            setItems([]);
        } finally {
            setLoadingList(false);
        }
    }, []);

    useEffect(() => {
        refreshUnread();
        const timer = window.setInterval(refreshUnread, POLL_MS);
        return () => window.clearInterval(timer);
    }, [refreshUnread]);

    useEffect(() => {
        return subscribeLiveNotifications((detail) => {
            if (!isAppInboxLiveNotification(detail)) {
                return;
            }
            refreshUnread();
            if (openRef.current) {
                loadList();
            }
            setToast({
                title: detail.title || 'Nova notificação',
                body: buildToastBody(detail),
                link: detail.link || '',
            });
        });
    }, [refreshUnread, loadList]);

    const handleOpen = async (event) => {
        setAnchorEl(event.currentTarget);
        await loadList();
        await refreshUnread();
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleMarkRead = async (id) => {
        setBusyId(id);
        try {
            await api.post(`/notifications/${id}/read`);
            setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
            setUnreadCount((prev) => Math.max(0, prev - 1));
        } finally {
            setBusyId(null);
        }
    };

    const handleMarkAllRead = async () => {
        setBusyId('all-read');
        try {
            await api.post('/notifications/mark-all-read');
            setItems((prev) => prev.map((n) => ({ ...n, read: true })));
            setUnreadCount(0);
        } finally {
            setBusyId(null);
        }
    };

    const handleRemoveAll = async () => {
        if (items.length === 0) {
            return;
        }
        if (!window.confirm('Excluir todas as notificações?')) {
            return;
        }
        setBusyId('all-delete');
        try {
            await api.post('/notifications/delete-all');
            setItems([]);
            setUnreadCount(0);
        } finally {
            setBusyId(null);
        }
    };

    const handleRemove = async (id) => {
        setBusyId(id);
        try {
            await api.delete(`/notifications/${id}`);
            const removed = items.find((n) => n.id === id);
            setItems((prev) => prev.filter((n) => n.id !== id));
            if (removed && !removed.read) {
                setUnreadCount((prev) => Math.max(0, prev - 1));
            }
        } finally {
            setBusyId(null);
        }
    };

    const handleOpenLink = async (notification) => {
        if (!notification.read) {
            await handleMarkRead(notification.id);
        }
        const target = resolveAppLink(notification.link);
        if (!target) {
            return;
        }
        handleClose();
        if (target.type === 'external') {
            window.open(target.href, '_blank', 'noopener,noreferrer');
            return;
        }
        const adminPath = target.href.startsWith('/admin')
            ? target.href
            : `/admin${target.href.startsWith('/') ? '' : '/'}${target.href}`;
        navigate(adminPath);
    };

    const handleToastClose = (_event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setToast(null);
    };

    const handleToastClick = () => {
        if (!toast?.link) {
            setToast(null);
            return;
        }
        const target = resolveAppLink(toast.link);
        setToast(null);
        if (!target) {
            return;
        }
        if (target.type === 'external') {
            window.open(target.href, '_blank', 'noopener,noreferrer');
            return;
        }
        navigate(target.href);
    };

    return (
        <>
            <Tooltip title="Notificações">
                <IconButton color="inherit" onClick={handleOpen} aria-label="Notificações">
                    <Badge badgeContent={unreadCount > 99 ? '99+' : unreadCount} color="error" invisible={unreadCount === 0}>
                        <NotificationsNone />
                    </Badge>
                </IconButton>
            </Tooltip>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ sx: { width: { xs: '92vw', sm: 380 }, maxHeight: 480 } }}
            >
                <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                        Notificações
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                        <Button
                            size="small"
                            startIcon={<DoneAll />}
                            onClick={handleMarkAllRead}
                            disabled={unreadCount === 0 || busyId === 'all-read' || busyId === 'all-delete'}
                        >
                            Ler todas
                        </Button>
                        <Button
                            size="small"
                            color="inherit"
                            startIcon={<DeleteOutline />}
                            onClick={handleRemoveAll}
                            disabled={items.length === 0 || busyId === 'all-read' || busyId === 'all-delete'}
                        >
                            Excluir todas
                        </Button>
                    </Box>
                </Box>
                <Divider />

                {loadingList ? (
                    <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : items.length === 0 ? (
                    <Box sx={{ px: 2, py: 4 }}>
                        <Typography variant="body2" color="text.secondary" align="center">
                            Nenhuma notificação por aqui.
                        </Typography>
                    </Box>
                ) : (
                    <List dense disablePadding sx={{ overflowY: 'auto', maxHeight: 400 }}>
                        {items.map((item) => {
                            const hasLink = Boolean(String(item.link || '').trim());
                            return (
                                <ListItem
                                    key={item.id}
                                    alignItems="flex-start"
                                    sx={{
                                        bgcolor: item.read ? 'transparent' : 'action.hover',
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        pr: 1,
                                    }}
                                    secondaryAction={
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            {!item.read && (
                                                <Tooltip title="Marcar como lida">
                                                    <span>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleMarkRead(item.id)}
                                                            disabled={busyId === item.id}
                                                        >
                                                            <CheckCircleOutline fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Remover">
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleRemove(item.id)}
                                                        disabled={busyId === item.id}
                                                    >
                                                        <DeleteOutline fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                            {hasLink && (
                                                <Tooltip title="Abrir link">
                                                    <IconButton size="small" onClick={() => handleOpenLink(item)}>
                                                        <OpenInNew fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    }
                                >
                                    <ListItemText
                                        primary={
                                            <Typography variant="body2" fontWeight={item.read ? 500 : 700} sx={{ pr: 5 }}>
                                                {item.title}
                                            </Typography>
                                        }
                                        secondary={
                                            <>
                                                <Typography
                                                    component="span"
                                                    variant="body2"
                                                    color="text.secondary"
                                                    sx={{ display: 'block', whiteSpace: 'pre-wrap', pr: 5 }}
                                                >
                                                    {item.body}
                                                </Typography>
                                                <Typography component="span" variant="caption" color="text.disabled">
                                                    {formatWhen(item.created_at)}
                                                    {item.broadcast
                                                        ? ' · Todos'
                                                        : Array.isArray(item.target_plan_names) && item.target_plan_names.length > 0
                                                            ? ` · Planos: ${item.target_plan_names.join(', ')}`
                                                            : ''}
                                                </Typography>
                                            </>
                                        }
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </Popover>

            <Snackbar
                open={Boolean(toast)}
                autoHideDuration={4500}
                onClose={handleToastClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                sx={{ mb: { xs: 1, sm: 2 }, mr: { xs: 1, sm: 2 } }}
            >
                <Alert
                    onClose={handleToastClose}
                    severity="info"
                    variant="outlined"
                    icon={<NotificationsNone fontSize="inherit" />}
                    onClick={toast?.link ? handleToastClick : undefined}
                    sx={{
                        width: { xs: 'min(92vw, 360px)', sm: 360 },
                        bgcolor: 'background.paper',
                        boxShadow: 2,
                        cursor: toast?.link ? 'pointer' : 'default',
                        alignItems: 'flex-start',
                        '& .MuiAlert-message': { width: '100%' },
                    }}
                >
                    <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                        {toast?.title}
                    </Typography>
                    {toast?.body ? (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                            {toast.body}
                        </Typography>
                    ) : null}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NotificationInbox;
